import {array, boolean, number, object, string} from 'yup';

import {paginationV8nSchema} from '@shared/api';
import {stringOptionalNullable} from '@shared/utils';
import {
  AutoCancelStatus,
  CancelType,
  ChoiceVariant,
  MarketType,
  OrderAction,
  OrderItemRole,
  OrderKindType,
  OrderLabel,
  OrderType,
  TriggerType,
} from '../types';

export const autoCancelV8nSchema = object().shape({
  // The list endpoint omits it, the order is known from the context
  order_id: stringOptionalNullable,
  timeout_at: string().required(),
  status: string().oneOf(Object.values(AutoCancelStatus)).required(),
});

export const stopLossV8nSchema = object().shape({
  order_id: string().required(),
  type: string().oneOf(Object.values(TriggerType)).required(),
  price: number().integer().required(),
  status: string().oneOf(Object.values(AutoCancelStatus)).required(),
});

export const orderV8nSchema = object().shape({
  id: string().required(),
  created_at: string().required(),
  user_id: string().required(),
  action: string().oneOf(Object.values(OrderAction)).required(),
  type: string().oneOf(Object.values(OrderType)).required(),
  side: string().oneOf(Object.values(ChoiceVariant)).required(),
  event_id: string().required(),
  requested_items_quantity: number().integer().required(),
  requested_max_inv: number().integer().required(),
  matched_quantity: number().integer().required(),
  exited_quantity: number().integer().required(),
  price: number().integer().nullable(),
  slippage: number().integer().nullable().optional(),
  matching: string().oneOf(Object.values(MarketType)).nullable().optional(),
  labels: array().of(string().oneOf(Object.values(OrderLabel)).required()),
  auto_cancel: autoCancelV8nSchema.nullable(),
  stop_loss: stopLossV8nSchema.nullable(),
  take_profit: stopLossV8nSchema.nullable(),
});

export const v8nSchemaOfGetUserOrdersResponse = paginationV8nSchema.shape({
  array: array().of(orderV8nSchema).required(),
});

export const orderItemOwnershipV8nSchema = object().shape({
  owner_user_id: string().required(),
  owner_order_id: string().required(),
  acquired_at: string().required(),
  released_at: stringOptionalNullable,
  release_reason: stringOptionalNullable,
  release_order_id: stringOptionalNullable,
  seller_user_id: stringOptionalNullable,
  seller_order_id: stringOptionalNullable,
  is_current_order: boolean().optional(),
});

export const orderItemV8nSchema = object().shape({
  id: string().required(),
  created_at: string().required(),
  updated_at: string().required(),
  side: string().oneOf(Object.values(ChoiceVariant)).required(),
  role: string().oneOf(Object.values(OrderItemRole)).required(),
  price: number().integer().required(),
  real: number().integer().required(),
  promo: number().integer().required(),
  user_id: string().required(),
  counterparty_side: string().oneOf(Object.values(ChoiceVariant)).required(),
  counterparty_user_id: stringOptionalNullable,
  counterparty_order_id: stringOptionalNullable,
  ownership: array().of(orderItemOwnershipV8nSchema).optional(),
  pair_item_id: stringOptionalNullable,
});

export const v8nSchemaOfGetOrderItemsResponse = paginationV8nSchema.shape({
  array: array().of(orderItemV8nSchema).required(),
});

export const orderFullInfoV8nSchema = object().shape({
  id: string().required(),
  event_id: string().required(),
  action: string().oneOf(Object.values(OrderAction)).required(),
  start_date: string().required(),
  end_date: string().required(),
  type: string().oneOf(Object.values(OrderKindType)).required(),
  side: string().oneOf(Object.values(ChoiceVariant)).required(),
  requested_items_quantity: number().integer().nullable(),
  executed_items_quantity: number().integer().nullable(),
  requested_max_inv: number().integer().nullable(),
  executed_max_inv: number().integer().nullable(),
  investment: number().integer().nullable(),
  invested: number().integer().nullable(),
  current_value: number().integer().nullable(),
  live_profit: number().integer().nullable(),
  auto_cancel: autoCancelV8nSchema.nullable(),
  stop_loss: stopLossV8nSchema.nullable(),
  take_profit: stopLossV8nSchema.nullable(),
  price: number().integer().nullable(),
  slippage: number().integer().nullable(),
  selling_price: number().integer().nullable(),
  potential_profit: number().integer().nullable(),
  avg_exit_price: number().integer().nullable(),
  unfilled: number().integer().nullable(),
  matched_quantity: number().integer().nullable(),
  matching: string().oneOf(Object.values(MarketType)).nullable(),
  labels: array().of(string().oneOf(Object.values(OrderLabel)).required()),
  profit: number().integer().nullable(),
  cancelled: string().oneOf(Object.values(CancelType)).nullable(),
});
